import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ReloadOutlined } from "@ant-design/icons";
import { Button, Form, Input, Space, theme } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { ERouteHttpVerb } from "@devowl-wp/api";
import { isEuVatIdSyntax, isSwitzerlandCompanyIdentificationNumberSyntax } from "@devowl-wp/vat-id";
import { RealCommerceError } from "../../../../exceptions/realCommerceError.js";
import { __ } from "../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../utils/matomoMask.js";
import { realCommerceRequest } from "../../../../utils/realCommerceRequest.js";
const VAT_ID_FIELD = "vatId";
const routeLocationCheckoutVatIdCheckPost = {
    path: "/checkout/vat-id-check",
    method: ERouteHttpVerb.POST
};
/**
 * VAT ID input with debounced client-side syntax check and a server-side
 * registry lookup against VIES / Swiss UID. The field is optional even for
 * companies; an empty value resolves immediately and shows no feedback icon.
 * Transient lookup errors expose an inline retry button that re-runs the
 * form validator.
 */ const VatIdField = ({ form })=>{
    const { token } = theme.useToken();
    const [lookupState, setLookupState] = useState("idle");
    // Mirrors the validator's thrown message for the `retryable` branch so the
    // custom inline-Retry `help` renderer can show the same copy without
    // duplicating the translation string between two places.
    const [retryableMessage, setRetryableMessage] = useState(null);
    const latestValidationRef = useRef(0);
    const currentValue = Form.useWatch(VAT_ID_FIELD, form);
    // Reset feedback as soon as the field becomes empty, independent of the
    // validator's debounce; otherwise an earlier success/error icon would
    // linger until the next 1 s tick.
    useEffect(()=>{
        if (!currentValue || currentValue.trim() === "") {
            latestValidationRef.current += 1;
            setLookupState("idle");
            setRetryableMessage(null);
        }
    }, [
        currentValue
    ]);
    const runServerCheck = useCallback(async (value)=>{
        try {
            const response = await realCommerceRequest({
                location: routeLocationCheckoutVatIdCheckPost,
                request: {
                    vatId: value
                }
            });
            return {
                ok: true,
                response
            };
        } catch (e) {
            return {
                ok: false,
                error: new RealCommerceError(e)
            };
        }
    }, []);
    const validator = useCallback(async (_, rawValue)=>{
        const value = typeof rawValue === "string" ? rawValue.trim() : "";
        if (!value) {
            setLookupState("idle");
            setRetryableMessage(null);
            return;
        }
        const isSyntaxValid = isEuVatIdSyntax(value) || isSwitzerlandCompanyIdentificationNumberSyntax(value);
        if (!isSyntaxValid) {
            setLookupState("invalid");
            setRetryableMessage(null);
            throw new Error(__("Please enter a valid VAT ID."));
        }
        const runId = latestValidationRef.current + 1;
        latestValidationRef.current = runId;
        setLookupState("checking");
        setRetryableMessage(null);
        const result = await runServerCheck(value);
        if (runId !== latestValidationRef.current) {
            // A newer validation superseded this one; drop the result.
            return;
        }
        if (result.ok) {
            setLookupState("ok");
            setRetryableMessage(null);
            return;
        }
        const { error } = result;
        if (error.hasErrorCode("CheckoutVatIdInvalidFormat")) {
            setLookupState("invalid");
            setRetryableMessage(null);
            throw new Error(__("Please enter a valid VAT ID."));
        }
        if (error.hasErrorCode("CheckoutVatIdNotRegistered")) {
            setLookupState("invalid");
            setRetryableMessage(null);
            throw new Error(__("This VAT ID is not registered. Please double-check it."));
        }
        if (error.hasErrorCode("CheckoutVatIdLookupUnavailable") || error.isBackendDown) {
            const message = __("The VAT register is temporarily unavailable. Please try again in a moment.");
            setLookupState("retryable");
            setRetryableMessage(message);
            throw new Error(message);
        }
        const fallbackMessage = __("Could not validate the VAT ID right now. Please try again in a moment.");
        setLookupState("retryable");
        setRetryableMessage(fallbackMessage);
        throw new Error(fallbackMessage);
    }, [
        runServerCheck
    ]);
    const handleRetry = useCallback(()=>{
        form.validateFields([
            VAT_ID_FIELD
        ]).catch(()=>{
        // Validation errors surface through Form.Item; ignore the promise rejection.
        });
    }, [
        form
    ]);
    const validateStatus = lookupState === "checking" ? "validating" : lookupState === "ok" ? "success" : lookupState === "invalid" || lookupState === "retryable" ? "error" : "";
    // The `retryable` branch renders the validator's message and the Retry
    // affordance on the same line via AntD's `help` slot — collapsing the
    // earlier two-line layout (red error + secondary "Having trouble…?" hint)
    // into a single row. `help` is undefined for every other state so AntD
    // falls back to the default validator-error rendering for `invalid`.
    // `paddingBottom` mirrors the breathing room the dropped `extra` slot used
    // to provide between the form item and whatever sits below it.
    const help = lookupState === "retryable" && retryableMessage ? /*#__PURE__*/ _jsxs(Space, {
        size: 4,
        style: {
            display: "inline-flex",
            alignItems: "baseline",
            flexWrap: "wrap",
            paddingBottom: 8
        },
        children: [
            /*#__PURE__*/ _jsx("span", {
                children: retryableMessage
            }),
            /*#__PURE__*/ _jsx(Button, {
                type: "link",
                size: "small",
                icon: /*#__PURE__*/ _jsx(ReloadOutlined, {}),
                onClick: handleRetry,
                style: {
                    padding: 0,
                    height: "auto"
                },
                children: __("Retry")
            })
        ]
    }) : undefined;
    // The why-we-need-it hint sits inline with the label as a small muted
    // suffix — same pattern as the Postal code label — so the field stays
    // compact but the motivation is still visible at a glance.
    const label = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            __("VAT ID (optional)"),
            /*#__PURE__*/ _jsxs("span", {
                style: {
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    marginLeft: 6,
                    fontWeight: 400
                },
                children: [
                    "\xb7 ",
                    __("to determine whether VAT needs to be charged")
                ]
            })
        ]
    });
    // Announces the async lookup state to assistive tech — Antd's visual
    // `hasFeedback` icon alone is silent to screen readers.
    const ariaStatusMessage = lookupState === "checking" ? __("Checking VAT ID.") : lookupState === "ok" ? __("VAT ID is valid.") : lookupState === "invalid" ? __("VAT ID is invalid.") : lookupState === "retryable" ? __("Could not validate the VAT ID right now.") : "";
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Form.Item, {
                name: VAT_ID_FIELD,
                label: label,
                validateDebounce: 1000,
                hasFeedback: true,
                validateStatus: validateStatus,
                help: help,
                rules: [
                    {
                        validator
                    }
                ],
                children: /*#__PURE__*/ _jsx(Input, {
                    placeholder: __("e.g. DE123456789"),
                    autoComplete: "off",
                    ...MATOMO_MASK
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                role: "status",
                "aria-live": "polite",
                className: "checkout-v2-sr-only",
                children: ariaStatusMessage
            })
        ]
    });
};
export { VatIdField, VAT_ID_FIELD };
